import React from 'react';
import { Bus, MapPin, Clock, Armchair } from 'lucide-react';
import { StorytellingLayout, ImageCarousel, ExpandableCard } from '../base';

/**
 * Tela de storytelling para ônibus/rodoviário
 * Responsabilidade: Exibir informações de viagem rodoviária
 * 
 * Props:
 * - block: dados do bloco de bus
 * - isOutbound: true = ida, false = volta
 * - stepIndicator, narrativeText, ctaText
 */

const DEFAULT_BUS_IMAGE = 'https://blog.livareviagens.com.br/wp-content/uploads/2021/12/pessoa-fotografando-paisagem-da-janela-de-onibus.jpg';

export default function StorytellingBus({
  block,
  isOutbound = true,
  stepIndicator,
  narrativeText,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const details = block.details || {};

  const images = block.images || [
    block.image_url || DEFAULT_BUS_IMAGE
  ];

  const departureCity = details.departure_city || block.title?.split('→')[0]?.trim() || 'Origem';
  const arrivalCity = details.arrival_city || block.title?.split('→')[1]?.trim() || 'Destino';

  const title = `${departureCity} → ${arrivalCity}`;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  const seatClassLabel = (cls) => {
    if (!cls) return 'Convencional';
    const map = {
      'leito': '🛏️ Leito',
      'semi-leito': '💺 Semi-leito',
      'semileito': '💺 Semi-leito',
      'convencional': '💺 Convencional',
      'executivo': '✨ Executivo',
      'leito - cama dd g8': '🛏️ Leito Cama DD G8',
      'leito - dd': '🛏️ Leito DD',
      'leito - cama dd': '🛏️ Leito Cama DD',
    };
    return map[cls.toLowerCase()] || cls;
  };

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Header: Ícone + Viação + Data */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Bus size={24} color="#5DD4C3" strokeWidth={2} />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Viação
          </p>
          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.company || 'Rodoviário'}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 0.25rem 0' }}>
            Data
          </p>
          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {formatDate(block.date)}
          </p>
        </div>
      </div>

      {/* Rota */}
      <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Partida */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <MapPin size={18} color="#5DD4C3" strokeWidth={2} fill="#5DD4C3" style={{ marginTop: '0.125rem' }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                Partida
              </p>
              <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0, lineHeight: 1.4 }}>
                {departureCity}
              </p>
              <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', margin: '0.125rem 0 0 0' }}>
                {details.departure_time || '--:--'}
              </p>
            </div>
          </div>

          {/* Linha conectora */}
          <div style={{ marginLeft: '0.5rem', borderLeft: '2px dashed rgba(255, 255, 255, 0.3)', height: '24px' }} />

          {/* Destino */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
            <MapPin size={18} color="rgba(255, 255, 255, 0.4)" strokeWidth={2} style={{ marginTop: '0.125rem' }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                Destino
              </p>
              <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0, lineHeight: 1.4 }}>
                {arrivalCity}
              </p>
              <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', margin: '0.125rem 0 0 0' }}>
                {details.arrival_time || ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info rápidas */}
      <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          {details.duration && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={16} color="#5DD4C3" strokeWidth={2} />
              <div>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Duração</p>
                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
                  {details.duration}
                </p>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Armchair size={16} color="#5DD4C3" strokeWidth={2} />
            <div>
              <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Classe</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
                {seatClassLabel(details.seat_class)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const expandedContent = (
    <p style={{
      fontSize: '0.8125rem',
      color: 'rgba(255, 255, 255, 0.7)',
      lineHeight: 1.6,
      margin: 0
    }}>
      • Poltrona reclinável com apoio para pernas<br/>
      • Ar condicionado e coberta<br/>
      • Tomadas USB e Wi-Fi (conforme viação)<br/>
      {details.platform ? `• Plataforma de embarque: ${details.platform}` : '• Rodoviária do ponto de embarque'}
    </p>
  );

  return (
    <StorytellingLayout
      backgroundImage={images[0]}
      title={title}
      narrativeText={narrativeText}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      <ImageCarousel images={images} />
      <ExpandableCard expandedContent={expandedContent}>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}
